/**
 * Power Outages layer (Ice Storm Risk → "Power Outages") — the first live render
 * of the E.1.9/E.1.10 `utility_outages` ingest (BC Hydro / Hydro-Québec / Hydro
 * One / NS Power + the municipal LDC adapters). The ambient ice-storm impact
 * signal: "where is power out" (operator-cop-doctrine §4.5).
 *
 * Per §3B.8 (founder revision 2026-06-05) outages live in the AMBIENT register:
 * EVERY non-restored outage reads as an AREA + a round dot SIZED by the
 * customers-affected band (size = magnitude), all in the outage red (Colour Law
 * 2 (b): dot OUTAGE_MAP_DOT_RED over area OUTAGE_MAP_AREA_RED — the map drops the
 * per-status hue), NEVER pulsing (motion is
 * reserved for live cameras + the scored L3/L4 Anomaly-Queue dots). Crew status
 * is carried by the panel badge + a small wrench glyph on the dot, not by colour.
 * RESTORED outages leave the map entirely.
 *
 * Area: where an upstream publishes a service-area footprint it renders as a faint
 * GROUND-CLAMPED polygon BENEATH the dot (classificationType: ClassificationType
 * .TERRAIN — the PR #65 ground-clamp pattern); otherwise an APPROXIMATE affected-
 * area disc (a fainter, outlined ellipse) so every outage still reads as an area
 * the way the utility maps show them. The dot stays the click target.
 *
 * Render gate (build.md § L.2 + §9.1, the raw-hotspots precedent): HIDDEN at
 * country zoom, visible regional+. Each dot + polygon carries a
 * distanceDisplayCondition with far = REGIONAL_VISIBLE_FAR_M so the national
 * Default view never clutters with outage dots.
 *
 * Imperative installer (MapView owns the viewer), mirroring RawHotspotsLayer:
 * requestRefresh on camera moves, setVisible on the toggle, pick from the central
 * click handler → UtilityOutageFeature → the UtilityOutageBody side panel.
 */
import * as Cesium from 'cesium';
import type { UtilityOutageFeature, UtilityOutageFeatureCollection } from '@tsr/shared-types';
import { fetchUtilityOutages, type BboxQuery, type UtilityOutageFetchOpts } from '../api';
import {
  outageDotSize,
  outageApproxRadiusM,
  OUTAGE_MAP_DOT_RED,
  OUTAGE_MAP_AREA_RED,
  OUTAGE_DOT_ALPHA,
  OUTAGE_AREA_ALPHA,
  OUTAGE_APPROX_AREA_ALPHA,
} from '../styles/outageStyles';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { renderLucideToDataUrl } from '../iconRegistry';
import { renderDiscGlyph } from '../discGlyphComposite';

const REFRESH_DEBOUNCE_MS = 300;
const OUTAGE_PREFIX = 'outage:';
const OUTAGE_AREA_PREFIX = 'outage-area:';
const OUTAGE_APPROX_PREFIX = 'outage-approx:';
const OUTAGE_CREW_PREFIX = 'outage-crew:';
/** White wrench glyph — the crew-dispatched STATUS sub-glyph, a small SUBORDINATE corner
 *  badge (#475 Field IC fold) so it never competes with the centred power identity disc; crew
 *  status still reads from shape, not colour (every non-restored outage is the same red).
 *  Founder NIGHT ruling 4 (2026-07-06): rendered FILLED (bold stroke) and one size step LARGER
 *  (10→14) so the crew read no longer disappears next to the disc. */
const CREW_GLYPH_SIZE = 14;
/** Bold stroke so the wrench reads as a FILLED, solid badge (NIGHT ruling 4), not a hairline. */
const CREW_GLYPH_STROKE = 3;

export interface PowerOutagesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: UtilityOutageFetchOpts,
  ) => Promise<UtilityOutageFeatureCollection>;
}

export interface PowerOutagesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its outage feature (null if not ours). */
  pick(entity: unknown): UtilityOutageFeature | null;
  destroy(): void;
}

/** GeoJSON MultiPolygon → Cesium PolygonHierarchy[] (one per polygon, with holes). */
function multiPolygonHierarchies(
  coords: number[][][][] | undefined,
): Cesium.PolygonHierarchy[] {
  if (!coords) return [];
  const out: Cesium.PolygonHierarchy[] = [];
  for (const polygon of coords) {
    const rings = polygon;
    if (!rings || rings.length === 0) continue;
    const outer = rings[0];
    if (!outer || outer.length < 3) continue;
    const positions = Cesium.Cartesian3.fromDegreesArray(outer.flat());
    const holes = rings
      .slice(1)
      .filter((r) => r && r.length >= 3)
      .map((r) => new Cesium.PolygonHierarchy(Cesium.Cartesian3.fromDegreesArray(r.flat())));
    out.push(new Cesium.PolygonHierarchy(positions, holes));
  }
  return out;
}

export function installPowerOutagesLayer(
  viewer: Cesium.Viewer,
  opts: PowerOutagesLayerOptions = {},
): PowerOutagesLayerHandle {
  const fetcher = opts.fetcher ?? fetchUtilityOutages;
  const ds = new Cesium.CustomDataSource('power-outages');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate). Shared by every
  // dot + polygon so the whole field tiers in together as the eye descends.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, UtilityOutageFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;

  // Colour Law 2 (b): a brighter DOT over a darker AREA fill — every non-restored
  // outage uses this pair (§3B.8). The dot is the click target; the area is faint.
  const dotColor = Cesium.Color.fromCssColorString(OUTAGE_MAP_DOT_RED);
  const areaColor = Cesium.Color.fromCssColorString(OUTAGE_MAP_AREA_RED);
  // White wrench glyph for crew-dispatched outages — FILLED (bold stroke) + one size larger
  // (NIGHT ruling 4). Null in unit tests where there's no canvas — the disc still renders, crew
  // just isn't glyphed.
  const crewGlyphUrl = renderLucideToDataUrl('wrench', {
    color: '#ffffff',
    size: CREW_GLYPH_SIZE,
    strokeWidth: CREW_GLYPH_STROKE,
  });

  const render = (fc: UtilityOutageFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      // Restored outages leave the map entirely (§3B.8 founder revision).
      if (feature.properties.status === 'restored') continue;
      const [lon, lat] = feature.geometry.coordinates;

      // Service-area footprint (when present) — a faint GROUND-CLAMPED polygon
      // beneath the dot. classificationType: TERRAIN drapes it on relief (the
      // PR #65 ground-clamp pattern) and keeps the dot above it the click target.
      const hierarchies = multiPolygonHierarchies(
        feature.service_area?.coordinates as number[][][][] | undefined,
      );
      if (hierarchies.length > 0) {
        for (let i = 0; i < hierarchies.length; i++) {
          const area = ds.entities.add({
            id: `${OUTAGE_AREA_PREFIX}${feature.id}:${i}`,
            polygon: {
              hierarchy: hierarchies[i],
              material: areaColor.withAlpha(OUTAGE_AREA_ALPHA),
              classificationType: Cesium.ClassificationType.TERRAIN,
              distanceDisplayCondition: gate,
            },
          });
          entityToFeature.set(area, feature);
        }
      } else {
        // No published footprint → an APPROXIMATE affected-area disc, sized by the
        // customers band. Rendered at height 0 (NOT clamped) with an outline ring
        // so it reads as approximate vs a solid clamped real footprint, and
        // fainter fill (§3B.8 — "every outage reads as an area, styled approximate").
        const radius = outageApproxRadiusM(feature.properties.customers_affected);
        const approx = ds.entities.add({
          id: `${OUTAGE_APPROX_PREFIX}${feature.id}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          ellipse: {
            semiMajorAxis: radius,
            semiMinorAxis: radius,
            height: 0,
            material: areaColor.withAlpha(OUTAGE_APPROX_AREA_ALPHA),
            outline: true,
            outlineColor: areaColor.withAlpha(0.55),
            outlineWidth: 2,
            distanceDisplayCondition: gate,
          },
        });
        entityToFeature.set(approx, feature);
      }

      // The outage MARKER — the EXACT-ACT disc every hazard family uses (founder NIGHT ruling 4,
      // 2026-07-06: unify the outage family onto the marker system — the old white-plate zone chip
      // is RETIRED here). A solid outage-red disc with the white `power` bolt knocked out at centre,
      // SIZED by the customers-affected band (magnitude — outages carry no Response Level), NEVER
      // pulsing (§3B.8 motion budget). Canvas-free (svg data-URL through the shared compositor), so
      // it renders in unit tests too and byte-matches the contact sheet. It sits above its ambient
      // area and is the click target. `renderDiscGlyph` is null only for an unknown glyph (never for
      // `power`) — the fallback keeps a plain outage-red dot so the outage always shows.
      const markerSize = outageDotSize(feature.properties.customers_affected);
      const discImage = renderDiscGlyph('power', { fill: OUTAGE_MAP_DOT_RED });
      const marker = ds.entities.add({
        id: `${OUTAGE_PREFIX}${feature.id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        billboard: discImage
          ? {
              image: discImage,
              width: markerSize,
              height: markerSize,
              // the disc IMAGE carries the outage red; the WHITE multiplier shows it at the ambient
              // alpha (no pulse — §3B.8 reserves motion for live cameras + scored L3/L4 dots).
              color: Cesium.Color.WHITE.withAlpha(OUTAGE_DOT_ALPHA),
              verticalOrigin: Cesium.VerticalOrigin.CENTER,
              horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              // L.2 render gate: hidden at country zoom, visible regional+.
              distanceDisplayCondition: gate,
            }
          : undefined,
        point: discImage
          ? undefined
          : {
              pixelSize: markerSize,
              color: dotColor.withAlpha(OUTAGE_DOT_ALPHA),
              outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
              outlineWidth: 1,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              distanceDisplayCondition: gate,
            },
      });
      entityToFeature.set(marker, feature);

      // Crew-dispatched → the small wrench SUB-badge, a subordinate corner badge (#475) so it never
      // competes with the centred power disc. FILLED (bold) + one size larger (NIGHT ruling 4).
      // Distinct entity (so it never disturbs the disc's click target, and — being NON-primary — the
      // co-location fan never offsets it), added AFTER the disc so it sorts on top, offset to the
      // bottom-right corner of the disc.
      if (feature.properties.status === 'crew_dispatched' && crewGlyphUrl) {
        const crew = ds.entities.add({
          id: `${OUTAGE_CREW_PREFIX}${feature.id}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          billboard: {
            image: crewGlyphUrl,
            width: CREW_GLYPH_SIZE,
            height: CREW_GLYPH_SIZE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            // Subordinate badge at the disc's bottom-right corner (scaled to the disc size).
            pixelOffset: new Cesium.Cartesian2(markerSize * 0.5, markerSize * 0.5),
            distanceDisplayCondition: gate,
          },
        });
        entityToFeature.set(crew, feature);
      }
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchUtilityOutages failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): UtilityOutageFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
